import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useRegisterOverlay } from '@/contexts/OverlayStackContext';
import showToast from '@/components/ui/Toast';
import wilayahService, { type KotaOption } from '@/services/wilayahService';
import { getCurrentCoordinates, reverseGeocodeCity } from '@/utils/geolocation';
import { setHomeCityName } from '@/utils/homeCityStorage';
import { impactLight } from '@/utils/nativeHaptics';
import { shouldDismissSheet } from '@/utils/sheetDismiss';

type LocationStep = 'enable' | 'manual';

interface HomeLocationSheetsProps {
  open: boolean;
  currentCity: string;
  onClose: () => void;
  onCityChange: (name: string) => void;
}

const PRESS = 'active:scale-[0.98] active:opacity-90 transition-transform';

export function HomeLocationSheets({
  open,
  currentCity,
  onClose,
  onCityChange,
}: HomeLocationSheetsProps) {
  const [step, setStep] = useState<LocationStep>('enable');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<KotaOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ tracking: false, startX: 0, startY: 0, dy: 0, dx: 0 });

  useEffect(() => {
    if (open) {
      setStep('enable');
      setQuery('');
      setResults([]);
    }
  }, [open]);

  useRegisterOverlay(open, () => {
    if (step === 'manual') {
      setStep('enable');
      return;
    }
    onClose();
  });

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      const kota = await wilayahService.searchKota(trimmed);
      if (!cancelled) {
        setResults(kota);
        setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const resetDrag = () => {
    drag.current = { tracking: false, startX: 0, startY: 0, dy: 0, dx: 0 };
    if (panelRef.current) {
      panelRef.current.style.transform = '';
      panelRef.current.style.transition = 'transform 0.2s ease-out';
    }
  };

  const onDragStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    drag.current = {
      tracking: true,
      startX: touch.clientX,
      startY: touch.clientY,
      dy: 0,
      dx: 0,
    };
    if (panelRef.current) {
      panelRef.current.style.transition = 'none';
    }
  };

  const onDragMove = (event: React.TouchEvent) => {
    if (!drag.current.tracking) {
      return;
    }
    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    const dy = touch.clientY - drag.current.startY;
    const dx = touch.clientX - drag.current.startX;
    drag.current.dy = dy;
    drag.current.dx = dx;
    if (dy <= 0 || Math.abs(dx) > dy) {
      return;
    }
    if (panelRef.current) {
      panelRef.current.style.transform = `translateY(${dy}px)`;
    }
  };

  const onDragEnd = () => {
    if (!drag.current.tracking) {
      return;
    }
    const { dy, dx } = drag.current;
    if (shouldDismissSheet({ deltaY: dy, deltaX: dx })) {
      resetDrag();
      onClose();
      return;
    }
    resetDrag();
  };

  const handleGps = async () => {
    setGpsLoading(true);
    try {
      const coords = await getCurrentCoordinates();
      if (!coords) {
        showToast.error('Tidak bisa mengambil lokasi. Coba izinkan GPS di pengaturan.');
        return;
      }
      const city = await reverseGeocodeCity(coords.latitude, coords.longitude);
      if (!city) {
        showToast.error('Gagal membaca nama kota dari GPS.');
        return;
      }
      setHomeCityName({ name: city });
      onCityChange(city);
      onClose();
      showToast.success(`Lokasi diperbarui ke ${city}`);
    } catch {
      showToast.error('Gagal memperbarui lokasi.');
    } finally {
      setGpsLoading(false);
    }
  };

  const handleSelectCity = (kota: KotaOption) => {
    const name = kota.namaLengkap || kota.nama || kota.label;
    impactLight();
    setHomeCityName({ name, code: kota.value });
    onCityChange(name);
    onClose();
    showToast.success(`Lokasi diperbarui ke ${name}`, { haptic: false });
  };

  const emptyHint = useMemo(() => {
    if (query.trim().length < 3) {
      return 'Ketik minimal 3 huruf…';
    }
    if (searching) {
      return 'Mencari kota…';
    }
    return 'Kota tidak ditemukan';
  }, [query, searching]);

  if (!open) {
    return null;
  }

  const sheet = (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="w-full max-w-md bg-white rounded-t-3xl px-5 pt-3 pb-safe-4 shadow-2xl max-h-[85vh] overflow-y-auto flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          data-testid="location-sheet-drag"
          className="flex justify-center mb-3 py-2 -mt-2"
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          onTouchCancel={onDragEnd}
        >
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {step === 'enable' ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#FFE8DE] text-[#FC480D] flex items-center justify-center mb-4">
              <Icon icon="ph:navigation-arrow-fill" className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold text-[#0E1726]">Aktifkan lokasi</h2>
            <p className="mt-1 text-sm text-gray-500">Lokasi saat ini: {currentCity}</p>
            <p className="mt-3 text-sm text-gray-500 leading-relaxed">
              Izinkan GPS supaya lapangan, mabar, dan komunitas terdekat lebih akurat. Kamu bisa
              mematikannya kapan saja di pengaturan perangkat.
            </p>
            <button
              type="button"
              onClick={() => void handleGps()}
              disabled={gpsLoading}
              className={`mt-5 w-full h-12 shrink-0 rounded-2xl bg-[#FC480D] text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60 ${PRESS}`}
            >
              <Icon icon="ph:crosshair" className="w-5 h-5" />
              {gpsLoading ? 'Mengambil lokasi…' : 'Gunakan lokasi saya'}
            </button>
            <button
              type="button"
              onClick={() => setStep('manual')}
              className={`mt-2.5 mb-2 w-full h-12 shrink-0 rounded-2xl bg-white border border-gray-200 text-[#0E1726] font-semibold text-sm flex items-center justify-center gap-2 ${PRESS}`}
            >
              <Icon icon="ph:map-pin" className="w-5 h-5 text-gray-500" />
              Pilih kota manual
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-[#0E1726]">Pilih kota</h2>
              <button
                type="button"
                onClick={() => void handleGps()}
                disabled={gpsLoading}
                className={`text-sm font-semibold text-[#FC480D] disabled:opacity-60 ${PRESS}`}
              >
                {gpsLoading ? 'Memuat…' : 'Pakai GPS'}
              </button>
            </div>
            <div className="relative mb-2">
              <Icon
                icon="ph:magnifying-glass"
                className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Cari kota atau kabupaten"
                className="w-full h-11 rounded-full border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm outline-none focus:outline-none focus:border-[#FC480D]"
              />
            </div>
            <div className="overflow-y-auto min-h-[220px]">
              {results.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">{emptyHint}</p>
              ) : (
                results.map((kota) => {
                  const name = kota.namaLengkap || kota.nama || kota.label;
                  const selected = name === currentCity || kota.nama === currentCity;
                  return (
                    <button
                      key={kota.value}
                      type="button"
                      onClick={() => handleSelectCity(kota)}
                      className={`w-full flex items-center gap-3 py-3.5 border-b border-gray-100 text-left ${PRESS}`}
                    >
                      <Icon
                        icon="ph:map-pin-fill"
                        className={`w-5 h-5 shrink-0 ${selected ? 'text-[#FC480D]' : 'text-gray-400'}`}
                      />
                      <span className="flex-1 font-bold text-[#0E1726]">{name}</span>
                      {selected ? (
                        <span className="w-6 h-6 rounded-full bg-[#FC480D] text-white flex items-center justify-center">
                          <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-[#FC480D]" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
